import { EntityIcon } from "@/components/ui/cores";
import { PageHeader } from '@/components/ui/page-header';
import { FormActions } from '@/components/ui/form-actions';
import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useVoltar } from '@/utils/voltar';
import { UsuarioService } from '../../services';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Row } from '@/components/grid/row';
import { FormikInput } from '@/components/ui/input';
import { Usuario } from '@/types/usuario';
import { FormikInputCpf } from '@/components/ui/formik-input';
import { getTenant } from '@/utils/TenantUtils';
import { PermissoesSection } from './PermissoesSection';
import { SenhaSection } from './SenhaSection';
import { SectionTitle } from '@/components/ui/section-title';
import { Checkbox } from '@/components/ui/checkbox';
import { KeyRound } from 'lucide-react';
import useAuth from '@/components/hooks/use.auth';
import { podeAlterarSenha, usePermissao } from '@/utils/permissao';

export const clienteFormValidationSchema = Yup.object().shape({
    // Usuário novo: nome e sobrenome (sem sobrenome o Keycloak pede "complete seu cadastro" no primeiro login)
    nome: Yup.string().required('Nome é obrigatório')
        .when('id', { is: (id?: string) => !id, then: schema => schema.matches(/\S\s+\S/, 'Informe nome e sobrenome') }),
    email: Yup.string().required('E-mail é obrigatório').email('Email inválido'),
    cpf: Yup.string().required('CPF é obrigatório'),
    senha: Yup.string().when('definirSenha', {
        is: true,
        then: schema => schema.required('Senha é obrigatória').min(8, 'A senha deve ter pelo menos 8 caracteres')
    })
});

// Só no cadastro: definir a senha agora ou deixar o Keycloak enviar o e-mail para a pessoa definir
type UsuarioForm = Usuario & { definirSenha?: boolean; senha?: string; senhaTemporaria?: boolean };

const initialValues: UsuarioForm = {
    nome: '',
    email: '',
    cpf: '',
    definirSenha: false,
    senha: '',
    senhaTemporaria: true
};

const dataHora = (iso: string) => {
    const d = new Date(iso);
    return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

type UsuarioFormPageParams = {
    
}

const UsuarioFormPage: React.FC<UsuarioFormPageParams> = () => {
    const [usuario, setUsuario] = useState<UsuarioForm>(initialValues);
    const [loading, setLoading] = useState<boolean>(false);
    const [permissoes, setPermissoes] = useState<UsuarioService.Permissoes>();
    const [aceite, setAceite] = useState<UsuarioService.Aceite | null>();
    const voltar = useVoltar();
    const {id, tenant} = useParams();
    const auth = useAuth();
    const somenteLeitura = !usePermissao('USUARIO_WRITE');
    const novo = !id || id === 'novo';

    useEffect(() => {
        if (id && id !== 'novo') {
            setLoading(true);
            
            UsuarioService.getByCodigo(id)
              .then(result => {
                setUsuario(result.data);
              })
              .catch(error => {
                  console.error(error);
              })
              .finally(() => setLoading(false));
            // Sem permissão de ver usuários (ex.: "Minha conta") a seção simplesmente não aparece
            UsuarioService.getPermissoes(id).then(r => setPermissoes(r.data)).catch(() => setPermissoes(undefined));
            UsuarioService.getAceite(id).then(r => setAceite(r.data || null)).catch(() => setAceite(undefined));
        }        
    },[id]);

    const onSave = ({definirSenha, senha, senhaTemporaria, ...usuarioDTO}: UsuarioForm) => {
        setLoading(true);
        if (novo){
            UsuarioService.create(definirSenha ? {...usuarioDTO, senha, senhaTemporaria} : usuarioDTO)
            .then(() => {
                toast.success("Usuário cadastrado com sucesso.", {
                    description: definirSenha
                        ? 'Se o e-mail já tinha conta, a senha dele não foi alterada.'
                        : `Quem ainda não tem conta recebe em ${usuarioDTO.email} o link para definir a senha.`
                })
                voltar(`/${getTenant()}/usuario`);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => setLoading(false));
        } else {
            UsuarioService.update(usuarioDTO, id!)
            .then(result => permissoes && !permissoes.administrador
                ? UsuarioService.alterarPermissoes(id!, permissoes.permissoes).then(() => result)
                : result)
            .then(result => {
                setUsuario(result.data);            
                toast.success("Usuário alterado com sucesso.")
                voltar(`/${getTenant()}/usuario`);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => setLoading(false));
        }
    }

    return (
        <div className="space-y-6">
            <PageHeader icon={<EntityIcon entidade="usuario" />} title={somenteLeitura ? 'Usuário' : novo ? 'Novo usuário' : 'Editar usuário'} />
            <Card className='p-6 shadow-none'>
                <Formik initialValues={usuario} validationSchema={clienteFormValidationSchema} onSubmit={onSave} enableReinitialize>
                    {(formikProps: FormikProps<UsuarioForm>) => (
                            <>
                            <Form>
                                <fieldset disabled={somenteLeitura} className="min-w-0">
                                <Row>
                                    <FormikInput name='nome' label={'Nome'} size={12} />
                                </Row>
                                <Row>
                                    <FormikInputCpf name='cpf' label='CPF' size={6} />
                                    <FormikInput name='email' label='Email' size={6} disabled={!novo} title={novo ? undefined : 'O e-mail é o login e não pode ser alterado.'} />
                                </Row>
                                {aceite !== undefined && (
                                    <p className="mt-3 text-sm text-muted-foreground">{aceite ? `Termos aceitos: versão ${aceite.versao} em ${dataHora(aceite.aceitoEm)}` : 'Ainda não aceitou os termos'}</p>
                                )}
                                {novo && (
                                    <>
                                    <SectionTitle icon={<KeyRound />} cor="bg-amber-500/10 text-amber-600 dark:text-amber-400"
                                        description="Se o e-mail já tiver conta, a pessoa só ganha acesso a esta corretora e a senha dela não muda.">
                                        Senha
                                    </SectionTitle>
                                    <label className="flex items-center gap-2.5 text-sm">
                                        <Checkbox checked={formikProps.values.definirSenha} onCheckedChange={v => formikProps.setFieldValue('definirSenha', v === true)} />
                                        Definir a senha agora (sem isso, a pessoa recebe um e-mail para definir a senha)
                                    </label>
                                    {formikProps.values.definirSenha && (
                                        <>
                                        <Row>
                                            <FormikInput name='senha' type='password' label='Senha' size={6} autoComplete='new-password' />
                                        </Row>
                                        <label className="flex items-center gap-2.5 text-sm">
                                            <Checkbox checked={formikProps.values.senhaTemporaria} onCheckedChange={v => formikProps.setFieldValue('senhaTemporaria', v === true)} />
                                            Pedir para trocar a senha no primeiro acesso
                                        </label>
                                        </>
                                    )}
                                    </>
                                )}
                                </fieldset>
                                {!novo && podeAlterarSenha(auth.user, tenant, id) && (
                                    <SenhaSection usuario={usuario} propria={id === auth.user?.id} />
                                )}
                                {permissoes && (
                                    <fieldset disabled={somenteLeitura} className="min-w-0">
                                        <PermissoesSection {...permissoes} onChange={lista => setPermissoes({ ...permissoes, permissoes: lista })} />
                                    </fieldset>
                                )}
                                <FormActions loading={loading} onSave={formikProps.submitForm} somenteLeitura={somenteLeitura} />
                            </Form>
                            </>
                        )}                
                    </Formik>
            </Card>            
        </div>
    );
}

export default UsuarioFormPage;